import { Box, Button, Checkbox, FormControl, FormErrorMessage, FormLabel, Input, Link, VStack } from '@chakra-ui/react';
import { RegisterData, useUserContext } from '@providers/useUserContext';
import FloatingLabelInput from 'components/FloatingLabelInput';
import { clearPendingReferral, rememberPendingReferral, useReferralContext } from 'hooks/useReferralContext';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-number-input';
import { useLocation, useNavigate } from 'react-router';
import { ApiClientExceptionErrorCode } from 'types/api';
import { ApiClientException } from 'utils/apiClientException';
import { resolveUserError } from 'utils/userErrorMessages';
import { ReferalTypeSelect } from '../ReferalTypeSelect';
import('react-phone-number-input/style.css');

export function Register() {
  const { state: locationState } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const referralContext = useReferralContext();
  // L'Id Mindbody d'un client n'est pas toujours numérique : les anciens
  // profils migrés ont un Id comme `MSC444660`. Il part tel quel au serveur ;
  // un `Number()` le transformait en NaN et le parrain était perdu sans erreur
  // (cas Magali Marti, Riise Paris, 30/09/2026).
  const ReferralClientId = referralContext.referrerClientId || null;
  const ReferralCampaignId = referralContext.campaignId;

  const methods = useForm<RegisterData>({
    defaultValues: {
      ScheduleEmails: true,
      PromotionalEmails: true,
      ReferredBy: ReferralClientId ? 'Another Client' : undefined,
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors, isSubmitting },
  } = methods;

  const { signUp } = useUserContext();

  async function onSubmit(data: RegisterData) {
    try {
      if (ReferralClientId) data.ReferralClientId = ReferralClientId;
      if (ReferralCampaignId) data.ReferralCampaignId = ReferralCampaignId;
      await signUp(data);
      // Inscription faite avec le lien : le parrainage est déjà rattaché,
      // rien à réclamer après coup. (Pas en OAuth : là, une adresse déjà
      // connue aboutit à une connexion, qui doit pouvoir réclamer.)
      clearPendingReferral();
      if (locationState && locationState.onLoginRedirect) {
        navigate(locationState.onLoginRedirect, { state: null });
      } else {
        navigate('.');
      }
    } catch (error) {
      if (error instanceof ApiClientException) {
        const message = error.getMessage();

        if (error.ErrorCode === ApiClientExceptionErrorCode.EmailUsed) {
          // Arrivé par un lien avec une adresse déjà connue : il va devoir se
          // connecter, peut-être via « mot de passe oublié » dans un autre
          // onglet. On garde le parrainage pour le réclamer après connexion.
          if (referralContext.hasReferral) rememberPendingReferral();
          setError('Email', { message });
        } else {
          setError('root', { message });
        }
        return;
      }

      const { title, description } = resolveUserError('auth', error);
      setError('root', { message: description ? `${title}. ${description}` : title });
    }
  }
  return (
    <FormProvider {...methods}>
      <VStack className="signUpForm" align="stretch" as="form" onSubmit={handleSubmit(onSubmit)}>
        <FormControl isInvalid={!!errors.Email}>
          <FloatingLabelInput
            name="Email"
            label="E-mail*"
            type="email"
            registerOptions={{ required: t('Veuillez entrer un email valide') }}
            errorMessage={errors.Email?.message}
          />
        </FormControl>
        <FormControl>
          <FloatingLabelInput
            name="Password"
            label={t('Mot de passe') + '*'}
            type="password"
            registerOptions={{ required: 'Veuillez entrer un mot de passe valide' }}
            errorMessage={t('Veuillez entrer un mot de passe valide')}
          />
        </FormControl>

        <FormControl>
          <FloatingLabelInput name="FirstName" label="Prénom*" type="text" registerOptions={{ required: true }} />
        </FormControl>

        <FormControl>
          <FloatingLabelInput name="LastName" label="Nom*" type="text" registerOptions={{ required: true }} />
        </FormControl>

        <FormControl>
          <FloatingLabelInput
            name="BirthDate"
            label="Date de naissance*"
            type="date"
            registerOptions={{ required: true }}
          />
        </FormControl>

        <FormControl>
          <Box maxWidth={'400px'}>
            <FormLabel textTransform={'uppercase'}>Téléphone*</FormLabel>
            <PhoneInput
              international
              required
              inputComponent={Input}
              defaultCountry="FR"
              numberInputProps={register('MobilePhone')}
              onChange={(value) => {
                if (value) setValue('MobilePhone', value);
              }}
            />
          </Box>
        </FormControl>

        <ReferalTypeSelect />

        <VStack>
          <Box maxWidth="400px" width="100%">
            <FormControl textAlign={'left'}>
              <Checkbox borderColor="black" {...register('PromotionalEmails')} textAlign={'left'}>
                Recevoir les actualités, événements et offres
              </Checkbox>
            </FormControl>
          </Box>

          <Box maxWidth="400px" width="100%">
            <FormControl isInvalid={!!errors.TermsAndConditions} textAlign={'left'}>
              <Checkbox borderColor="black" {...register('TermsAndConditions', { required: true })} textAlign={'left'}>
                Je comprends et j’accepte les{' '}
                <Link textDecoration="underline" href="#">
                  conditions générales de vente*
                </Link>
              </Checkbox>
            </FormControl>
          </Box>
        </VStack>

        <FormControl isInvalid={!!errors.root} alignSelf="center" justifyContent={'center'} mt={1}>
          <FormErrorMessage textAlign="center" justifyContent={'center'}>
            {errors.root?.message}
          </FormErrorMessage>
        </FormControl>
        <VStack padding="2em 1em">
          <Button textAlign="center" margin="auto" type="submit" variant="contained" isLoading={isSubmitting}>
            Créer un compte
          </Button>
        </VStack>
      </VStack>
    </FormProvider>
  );
}
